/**
 * Desktop MONITORING (DESIGN-CHANGELOG 2026-09-03; `design/GT Mine.dc.html:
 * 266-347`). AA phase 4.
 *
 * Full-screen behind MY GAMES' second door. The page bar is navigation only;
 * the page's own first row is the stat (`12 GAMES · 19 TRIGGERS ARMED`), the
 * search box and `N ROWS`. Then the MATRIX: a 300px GAME column and seven
 * equal trigger columns in the design's order, a lamp per cell — armed is a
 * drawn block on accent, off an empty box, N/A a dash on a dashed border with
 * the reason as its tip. The PRICE cell is an in-place `− $N +` stepper while
 * armed. A column header click arms that trigger on every row that can carry
 * it. The right rail is WHAT FIRED: the board's WORLD rows, last 14 days.
 *
 * MONITORING IS DERIVED, NOT CURATED: a game appears the moment one trigger is
 * armed and leaves when the last goes off — after you leave the page, so a
 * row you just silenced does not vanish under the cursor (`monPin`).
 */

import { useEffect } from 'react';

import { CoverArt } from '../../components/CoverArt.tsx';
import { SearchBox } from '../../components/SearchBox.tsx';
import { useArt, useQueueArt } from '../../components/useArt.ts';
import { useSearchInputRef } from '../searchHotkey.ts';
import { useQd } from '../../store/index.ts';
import { monitoringVM } from '../../store/selectors.ts';
import type { MonRow } from '../../store/selectors.ts';
import { PageBar } from '../components/PageBar.tsx';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

function Row({ r }: { r: MonRow }): JSX.Element {
  const st = useQd();
  const art = useArt(r.id + 'p', r.coverCands);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '300px repeat(7, 1fr)', alignItems: 'center', borderBottom: '1px solid var(--gt-line-0)', minHeight: 54 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px 6px 0', minWidth: 0 }}>
        <div onClick={() => st.openGame(r.id)} style={{ position: 'relative', width: 28, height: 42, flex: 'none', background: r.fall, overflow: 'hidden', border: '1px solid var(--gt-line)', cursor: 'pointer' }}>
          <CoverArt style={{ position: 'absolute', inset: 0 }} fs={8} ini={r.ini} hasCover={art.hasCover} cover={art.cover} />
        </div>
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
            <span onClick={() => st.openGame(r.id)} style={{ fontFamily: 'var(--gt-font-sans)', fontWeight: 600, fontSize: 13, color: 'var(--gt-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}>
              {r.n}
            </span>
            <span style={{ fontFamily: MONO, fontSize: 7.5, letterSpacing: '.08em', color: r.srcClr, border: '1px ' + r.srcBs + ' ' + r.srcClr, padding: '2px 5px', flex: 'none' }}>{r.srcL}</span>
          </div>
          <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.06em', color: 'var(--gt-faint)' }}>{r.sub}</span>
        </div>
      </div>
      {r.cells.map((c) => (
        <div key={c.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 54, background: c.on ? 'var(--accSoft)' : 'transparent' }}>
          {c.isPrice && c.on ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ display: 'flex', border: '1px solid var(--acc)' }}>
                <span onClick={() => void st.monCap(r.id, -1)} style={{ width: 20, height: 22, display: 'grid', placeItems: 'center', fontFamily: MONO, fontSize: 10, color: 'var(--acc)', cursor: 'pointer' }}>−</span>
                <span onClick={() => void st.monTog(r.id, c.k)} data-tip={c.tip} style={{ minWidth: 30, height: 22, display: 'grid', placeItems: 'center', fontFamily: MONO, fontSize: 9.5, color: 'var(--acc)', borderLeft: '1px solid var(--acc)', borderRight: '1px solid var(--acc)', padding: '0 3px', cursor: 'pointer' }}>
                  {c.capTxt}
                </span>
                <span onClick={() => void st.monCap(r.id, 1)} style={{ width: 20, height: 22, display: 'grid', placeItems: 'center', fontFamily: MONO, fontSize: 10, color: 'var(--acc)', cursor: 'pointer' }}>+</span>
              </div>
              {c.capPct ? <span style={{ fontFamily: MONO, fontSize: 7.5, letterSpacing: '.05em', color: 'var(--gt-faint)' }}>{c.capPct}</span> : null}
            </div>
          ) : (
            <span
              onClick={() => (c.na ? undefined : void st.monTog(r.id, c.k))}
              data-tip={c.tip}
              style={{
                width: 22,
                height: 22,
                boxSizing: 'border-box',
                border: '1px ' + (c.na ? 'dashed' : 'solid') + ' ' + (c.on ? 'var(--acc)' : c.na ? 'var(--gt-border-2)' : 'var(--gt-tick)'),
                background: c.on ? 'var(--acc)' : 'transparent',
                display: 'grid',
                placeItems: 'center',
                fontFamily: MONO,
                fontSize: 9,
                color: c.on ? 'var(--gt-ink)' : c.na ? 'var(--gt-ghost)' : 'var(--gt-text-5)',
                cursor: c.na ? 'not-allowed' : 'pointer',
              }}
            >
              {c.on ? '■' : c.na ? '–' : c.isPrice ? '$' : ''}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Monitoring(): JSX.Element {
  const s = useQd();
  const v = monitoringVM(s);
  const ref = useSearchInputRef();
  const monEnter = useQd((st) => st.monEnter);
  // Pin the row set on entry: a row you silence stays until you leave.
  useEffect(() => monEnter(), [monEnter]);

  // Same art fetch the phone screen runs — see the note there.
  useQueueArt(v.rows.map((r) => r.id));

  return (
    <div data-screen-label="Monitoring" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <PageBar title="MONITORING" />
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 24px 0', flexWrap: 'wrap' }}>
        <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.07em', color: 'var(--acc)', whiteSpace: 'nowrap' }}>{v.monStat}</span>
        <div style={{ width: 1, height: 20, background: 'var(--gt-border-2)' }} />
        <SearchBox inputRef={ref} value={v.monQ} onChange={(e) => s.setMonQ(e.target.value)} onClear={() => s.setMonQ('')} placeholder="SEARCH MONITORED GAMES…" w="250px" pfs={11} ifs={10.5} />
        <div style={{ flex: 1 }} />
        <span style={{ fontFamily: MONO, fontSize: 9.5, color: 'var(--gt-faint)', whiteSpace: 'nowrap' }}>{v.monCountTxt}</span>
      </div>

      <div style={{ display: 'flex', gap: 24, padding: '14px 24px 30px', alignItems: 'flex-start' }}>
        {/* the matrix ------------------------------------------------------------ */}
        <div style={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
          <div style={{ minWidth: 700 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '300px repeat(7, 1fr)', borderBottom: '1px solid var(--gt-line)', paddingBottom: 8 }}>
              <span style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '.09em', color: 'var(--gt-faint)' }}>GAME</span>
              {v.cols.map((c) => (
                <div key={c.key} onClick={() => void s.monColTog(c.k)} data-tip={c.tip} className="d-hov-acc" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, cursor: 'pointer' }}>
                  <span style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '.09em', color: c.clr, whiteSpace: 'nowrap' }}>{c.l}</span>
                  <span style={{ fontFamily: MONO, fontSize: 7.5, letterSpacing: '.06em', color: 'var(--gt-ghost)' }}>{c.nOn}</span>
                </div>
              ))}
            </div>
            {v.rows.map((r) => (
              <Row key={r.key} r={r} />
            ))}
            {v.empty ? (
              <div style={{ padding: '30px 0', fontFamily: MONO, fontSize: 10, letterSpacing: '.07em', color: 'var(--gt-muted)' }}>{v.emptyTxt}</div>
            ) : null}
          </div>
        </div>

        {/* WHAT FIRED ----------------------------------------------------------- */}
        <div style={{ width: 300, flex: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{ fontFamily: PIXEL, fontSize: '9.5px', color: 'var(--gt-text)', transform: 'translateY(0.13em)' }}>WHAT FIRED</span>
            <span data-tip={v.firedTip} style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.08em', color: 'var(--gt-faint)' }}>{v.firedSub}</span>
          </div>
          <div style={{ borderTop: '1px solid var(--gt-line)' }}>
            {v.fired.map((f) => (
              <div key={f.key} onClick={() => s.openGame(f.id)} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '10px 0', borderBottom: '1px solid var(--gt-line-0)', cursor: 'pointer' }}>
                <span style={{ fontFamily: MONO, fontSize: 7.5, letterSpacing: '.09em', color: f.tagClr, background: f.tagBg, border: '1px solid ' + f.tagClr, padding: '3px 5px', flex: 'none', marginTop: 1 }}>{f.tag}</span>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={{ fontFamily: 'var(--gt-font-sans)', fontWeight: 600, fontSize: 12.5, color: 'var(--gt-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.n}</span>
                  <span style={{ fontFamily: 'var(--gt-font-sans)', fontSize: 12.5, lineHeight: 1.45, color: 'var(--gt-muted)', textWrap: 'pretty' }}>{f.t}</span>
                </div>
                <span style={{ fontFamily: MONO, fontSize: 9, color: 'var(--gt-tick)', flex: 'none' }}>{f.w}</span>
              </div>
            ))}
            {v.firedEmpty ? (
              <div style={{ padding: '22px 0', fontFamily: MONO, fontSize: 9.5, letterSpacing: '.07em', lineHeight: 1.8, color: 'var(--gt-muted)' }}>{v.firedEmptyTxt}</div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
